
import React, { useState, useEffect } from 'react';
import { AppSettings, User } from '../types';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: AppSettings;
  users: User[];
  onSave: (settings: AppSettings) => void;
  onAddUser: (user: User) => void;
  onUpdateUser: (user: User) => void;
  onDeleteUser: (id: string) => void;
  onTestTypeform: () => void;
  onTestGHL: () => void;
  onTestShopify: () => void;
  onTestWorker: () => void;
  onRepairDatabase: (mode: 'repair' | 'rebuild') => void;
  onDeleteAllRecords: () => void;
  onViewLogs: () => void;
  setConfirmConfig: (config: any) => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({ 
  isOpen, onClose, settings, users, onSave, onAddUser, onUpdateUser, onDeleteUser,
  onTestTypeform, onTestGHL, onTestShopify, onTestWorker, onRepairDatabase, onDeleteAllRecords, onViewLogs, setConfirmConfig
}) => {
  const [localSettings, setLocalSettings] = useState<AppSettings>(settings);
  const [ghlFields, setGhlFields] = useState<Array<{ id: string; name: string; fieldKey: string }>>([]);
  const [isLoadingFields, setIsLoadingFields] = useState(false);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const fetchGhlFields = async () => {
    if (!localSettings.ghlToken) {
      alert("Please enter a GHL Token first.");
      return;
    }
    // Location ID is required for V2, but V1 might work without it if using legacy key. 
    // We check it anyway to encourage V2 usage.
    if (!localSettings.ghlLocationId) {
      alert("Please enter a Location ID first.");
      return;
    }

    setIsLoadingFields(true);
    let proxy = localSettings.corsProxy;
    if (proxy && !proxy.endsWith('/')) proxy += '/';

    try {
       // STRATEGY 1: Try GHL API V2 (Preferred)
       // Requires 'locations/customFields.readonly' scope
       const v2Url = `${proxy}https://services.leadconnectorhq.com/locations/${localSettings.ghlLocationId}/customFields`;
       
       const v2Res = await fetch(v2Url, {
         headers: { 
            'Authorization': `Bearer ${localSettings.ghlToken}`, 
            'Version': '2021-07-28',
            'Accept': 'application/json' 
        }
       });
       
       if (v2Res.ok) {
          const data = await v2Res.json();
          if (data.customFields) {
            const sorted = data.customFields.sort((a: any, b: any) => a.name.localeCompare(b.name));
            setGhlFields(sorted);
            alert(`Success! Loaded ${sorted.length} fields (V2 API).`);
            setIsLoadingFields(false);
            return;
          }
       }

       // STRATEGY 2: Fallback to GHL API V1 (Legacy)
       // Useful if user is using a Location API Key instead of OAuth Token
       console.log(`V2 Failed (${v2Res.status}), attempting V1 fallback...`);
       
       const v1Url = `${proxy}https://rest.gohighlevel.com/v1/custom-fields/`;
       const v1Res = await fetch(v1Url, {
          headers: { 
             'Authorization': `Bearer ${localSettings.ghlToken}`
          }
       });

       if (v1Res.ok) {
          const data = await v1Res.json();
          if (data.customFields) {
            const sorted = data.customFields.sort((a: any, b: any) => a.name.localeCompare(b.name));
            setGhlFields(sorted);
            alert(`Success! Loaded ${sorted.length} fields (Legacy V1 API).`);
            setIsLoadingFields(false);
            return;
          }
       }

       // If both fail, throw error based on V2 response
       const txt = await v2Res.text();
       throw new Error(`API V2 Error ${v2Res.status}: ${txt}\n(Also failed Legacy V1 fallback). Check Token Scopes (locations/customFields.readonly).`);

    } catch (e: any) {
      console.error("Error fetching GHL fields", e);
      alert(`Failed to load fields: ${e.message}`);
    } finally {
      setIsLoadingFields(false);
    }
  };

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(localSettings);
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-black/60 backdrop-blur-md"
      onClick={onClose}
    >
      <div 
        className="w-full max-w-xl bg-white rounded-[2.5rem] border border-gray-100 shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-10 py-8 border-b border-purple-50 flex justify-between items-center bg-purple-50/50 sticky top-0 z-10 backdrop-blur-md">
          <div>
            <h2 className="text-xl font-bold text-purple-900 tracking-tight">System Configuration</h2>
            <p className="text-[10px] font-black uppercase tracking-widest text-purple-400 mt-1">Full Integration Suite</p>
          </div>
          <button onClick={onClose} className="p-2 bg-white rounded-xl text-gray-400 hover:text-gray-900 transition-all shadow-sm">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="p-10 space-y-10 flex-1 overflow-y-auto scrollbar-hide">
          {/* Cloud Database */}
          <section className="space-y-6">
            <h3 className="text-xs font-black uppercase tracking-widest text-gray-900">Worker & Cloud Storage</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-2 ml-1">Worker URL</label>
                <input
                  type="text"
                  value={localSettings.workerUrl}
                  onChange={(e) => setLocalSettings({ ...localSettings, workerUrl: e.target.value })}
                  className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="https://your-worker.xxx.workers.dev"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-2 ml-1">Worker Secret</label>
                  <input
                    type="password"
                    value={localSettings.workerSecret}
                    onChange={(e) => setLocalSettings({ ...localSettings, workerSecret: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-2 ml-1">CORS Proxy</label>
                  <input
                    type="text"
                    value={localSettings.corsProxy}
                    onChange={(e) => setLocalSettings({ ...localSettings, corsProxy: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
            </div>
          </section>

          <div className="h-[1px] bg-purple-50"></div>

          {/* Airtable Section */}
          <section className="space-y-6">
            <h3 className="text-xs font-black uppercase tracking-widest text-gray-900">Airtable Settings</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-2 ml-1">API Key (PAT)</label>
                  <input
                    type="password"
                    value={localSettings.airtableApiKey}
                    onChange={(e) => setLocalSettings({ ...localSettings, airtableApiKey: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="pat.xxx..."
                  />
                </div>
                <div>
                  <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-2 ml-1">Base ID</label>
                  <input
                    type="text"
                    value={localSettings.airtableBaseId}
                    onChange={(e) => setLocalSettings({ ...localSettings, airtableBaseId: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-2 ml-1">Ambassador Table ID</label>
                <input
                  type="text"
                  value={localSettings.atAmbassadorTableId}
                  onChange={(e) => setLocalSettings({ ...localSettings, atAmbassadorTableId: e.target.value })}
                  className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
          </section>

          <div className="h-[1px] bg-purple-50"></div>

          {/* Typeform & GHL */}
          <section className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-xs font-black uppercase tracking-widest text-gray-900">Marketing & CRM</h3>
              <button 
                onClick={fetchGhlFields} 
                disabled={isLoadingFields}
                className="text-[9px] bg-purple-50 text-purple-600 px-3 py-1.5 rounded-lg font-bold uppercase tracking-widest hover:bg-purple-100 disabled:opacity-50 transition-all"
              >
                {isLoadingFields ? 'Loading...' : 'Load GHL Fields'}
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-2 ml-1">Typeform Token</label>
                  <input
                    type="password"
                    value={localSettings.typeformToken}
                    onChange={(e) => setLocalSettings({ ...localSettings, typeformToken: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-2 ml-1">Form ID</label>
                  <input
                    type="text"
                    value={localSettings.formId}
                    onChange={(e) => setLocalSettings({ ...localSettings, formId: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs outline-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-2 ml-1">GHL Token</label>
                  <input
                    type="password"
                    value={localSettings.ghlToken}
                    onChange={(e) => setLocalSettings({ ...localSettings, ghlToken: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-2 ml-1">Location ID</label>
                  <input
                    type="text"
                    value={localSettings.ghlLocationId}
                    onChange={(e) => setLocalSettings({ ...localSettings, ghlLocationId: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs outline-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-2 ml-1">GHL Amb Code Field</label>
                  {ghlFields.length > 0 ? (
                    <select
                      value={localSettings.ghlAmbassadorCodeFieldId}
                      onChange={(e) => setLocalSettings({ ...localSettings, ghlAmbassadorCodeFieldId: e.target.value })}
                      className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs outline-none focus:ring-2 focus:ring-purple-500 appearance-none"
                    >
                       <option value="">Select Code Field...</option>
                       {ghlFields.map(f => (
                         <option key={f.id} value={f.id}>{f.name} ({f.fieldKey})</option>
                       ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={localSettings.ghlAmbassadorCodeFieldId}
                      onChange={(e) => setLocalSettings({ ...localSettings, ghlAmbassadorCodeFieldId: e.target.value })}
                      className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="fld..."
                    />
                  )}
                </div>
                <div>
                  <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-2 ml-1">GHL Amb Pass Field</label>
                  {ghlFields.length > 0 ? (
                    <select
                      value={localSettings.ghlAmbassadorPasswordFieldId}
                      onChange={(e) => setLocalSettings({ ...localSettings, ghlAmbassadorPasswordFieldId: e.target.value })}
                      className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs outline-none focus:ring-2 focus:ring-purple-500 appearance-none"
                    >
                       <option value="">Select Password Field...</option>
                       {ghlFields.map(f => (
                         <option key={f.id} value={f.id}>{f.name} ({f.fieldKey})</option>
                       ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={localSettings.ghlAmbassadorPasswordFieldId}
                      onChange={(e) => setLocalSettings({ ...localSettings, ghlAmbassadorPasswordFieldId: e.target.value })}
                      className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="fld..."
                    />
                  )}
                </div>
              </div>
            </div>
          </section>

          <div className="h-[1px] bg-purple-50"></div>

          {/* Shopify Section */}
          <section className="space-y-6">
            <h3 className="text-xs font-black uppercase tracking-widest text-gray-900">Shopify Integration</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-2 ml-1">Store URL</label>
                <input
                  type="text"
                  value={localSettings.shopifyUrl}
                  onChange={(e) => setLocalSettings({ ...localSettings, shopifyUrl: e.target.value })}
                  className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="https://your-store.myshopify.com"
                />
              </div>
              <div>
                <label className="block text-[9px] font-black uppercase tracking-widest text-purple-400 mb-2 ml-1">Admin API Access Token</label>
                <input
                  type="password"
                  value={localSettings.shopifyAccessToken}
                  onChange={(e) => setLocalSettings({ ...localSettings, shopifyAccessToken: e.target.value })}
                  className="w-full bg-gray-50 border border-gray-100 rounded-2xl p-4 text-xs outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="shpat_xxx..."
                />
              </div>
            </div>
          </section>
        </div>

        <div className="px-10 py-8 bg-gray-50/50 border-t border-purple-50 flex gap-4 sticky bottom-0 z-10 backdrop-blur-md">
           <button onClick={onClose} className="flex-1 bg-white border border-gray-100 text-gray-500 rounded-full py-4 text-[10px] font-bold uppercase tracking-widest hover:bg-gray-100 transition-all">Cancel</button>
           <button onClick={handleSave} className="flex-1 bg-purple-600 text-white rounded-full py-4 text-[10px] font-bold uppercase tracking-widest shadow-lg shadow-purple-100 hover:bg-purple-500 transition-all">Save Settings</button>
        </div>
      </div>
    </div>
  );
};
